import { createContext, useContext, useState, ReactNode } from 'react';
import DBManager from '../Managers/DBManager';
import DeviceManager from '../Managers/DeviceManager';
import WebusbManager from '../Managers/WebusbManager';
import Popup from '../Components/popup';
import { getCachedTheme, setCachedTheme } from "../Utils/Cache";

export type PopupOptions = {
    title?: string;
    content: any;
    onConfirm?: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    titleIcon?: ReactNode;
    sub?: boolean;
    noSubBlur?: boolean;
    footerContent?: ReactNode;
    customClass?: string;
    noCancel?: boolean;
    onMouseEnter?: any;
    onMouseLeave?: any;
    noButtons?: boolean;
    closeIcon?: boolean;
};

export type GlobalContextType = {
    dbManager: DBManager;
    setDBManager: (dbManager: DBManager) => void;
    deviceManager: DeviceManager;
    setDeviceManager: (deviceManager: DeviceManager) => void;
    usbManager: WebusbManager;
    setUsbManager: (usbManager: WebusbManager) => void;
    showPopup: (options: PopupOptions) => void;
    hidePopup: (isSub?: boolean) => void;
    popup: ReactNode;
    subPopup: ReactNode;
    toggleTheme: () => void;
    getThemeStyles: () => any;
    getThemeImage: (image: string) => any;
    theme: 'light' | 'dark';
    connectedDevices: string[];
    setConnectedDevices: (devices: string[]) => void;
};

// Create the context
const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

// Define the provider props
interface ThemeContextProviderProps {
    children: ReactNode;
}

// Create the provider component
export default function GlobalContextProvider({ children }: ThemeContextProviderProps) {
    const [dbManager, setDBManager] = useState<DBManager>(new DBManager());
    const [deviceManager, setDeviceManager] = useState<DeviceManager>(new DeviceManager(dbManager));
    const [usbManager, setUsbManager] = useState<WebusbManager>(new WebusbManager(dbManager.log));
    const [popup, setPopup] = useState<ReactNode>(null);
    const [subPopup, setSubPopup] = useState<ReactNode>(null);
    const [theme, setTheme] = useState<'light' | 'dark'>(getCachedTheme());
    const [connectedDevices, setConnectedDevices] = useState<string[]>([]);

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
        setCachedTheme(theme === 'light' ? 'dark' : 'light');
    }

    const getThemeStyles = () => {
        const styles = {
            dark: {
                '--theme-color': '#6FFBB0',
                '--theme-bg-color': '#000',
                '--theme-bg-color-light': '#6FFBB040',
            },
            light: {
                '--theme-color': '#90044f',
                '--theme-bg-color': '#FFF',
                '--theme-bg-color-light': '#90044f40',
            }
        }
        return styles[theme] as React.CSSProperties;
    }

    const getThemeImage = (image: string) => {
        // add '_light' to the src if the theme is light
        return '/assets/' + image + (theme === 'light' ? '_light.png' : '.png');
    }

    const hidePopup = (isSub: boolean = false) => {
        if (isSub) {
            document.getElementById('MainPopup')?.classList.remove('blur');
            setSubPopup(null);
        } else {
            setPopup(null);
        }
    };

    const showPopup = (options: PopupOptions) => {
        const { 
            title, 
            content, 
            onConfirm, 
            onCancel, 
            confirmText, 
            cancelText, 
            titleIcon,
            footerContent,
            customClass,
            noCancel,
            onMouseEnter,
            onMouseLeave,
            sub,
            noSubBlur,
            noButtons,
            closeIcon
        } = options;

        const cancelAction = () => {
            hidePopup(options.sub);
            if (onCancel) onCancel();
        };

        const confirmAction = () => {
            hidePopup(options.sub);
            if (onConfirm) onConfirm();
        };

        const popup: ReactNode = (
            <Popup
                title={title}
                content={content}
                onConfirm={confirmAction}
                onCancel={cancelAction}
                confirmText={confirmText}
                cancelText={cancelText}
                showCancel={!!onConfirm && !noCancel}
                titleIcon={titleIcon}
                isSub={sub}
                footerContent={footerContent}
                customClass={customClass}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                noButtons={noButtons}
                closeIcon={closeIcon}
            />
        );

        if (sub) {
            if(!noSubBlur) document.getElementById('MainPopup')?.classList.add('blur');
            setSubPopup(popup);
        } else {
            setPopup(popup);
        }
    };

    return (
        <GlobalContext.Provider
            value={{
                dbManager,
                setDBManager,
                deviceManager,
                setDeviceManager,
                usbManager,
                setUsbManager,
                showPopup,
                hidePopup,
                popup,
                subPopup,
                toggleTheme,
                getThemeStyles,
                theme,
                connectedDevices,
                setConnectedDevices,
                getThemeImage
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
}

// Create the hook
export function useGlobalContext() {
    const context = useContext(GlobalContext);
    if (context === undefined) {
        throw new Error('useGlobalContext must be used within a GlobalContextProvider');
    }
    return context;
}